import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Search, ChevronDown, ChevronUp, FileText, ExternalLink,
  Bookmark, Copy, Loader2, Plus, X, Clock, RotateCw
} from 'lucide-react';
import EmptyState from '../components/EmptyState';
import ConfigDialog from '../components/ConfigDialog';

const TERRITORIES = ['Ticino', 'Svizzera', 'Europa', 'Internazionale'];
const LANGUAGES = ['IT', 'EN', 'DE', 'FR'];
const PERIODS = [
  { value: 'week', label: 'Ultima settimana' },
  { value: 'month', label: 'Ultimo mese' },
  { value: 'quarter', label: 'Ultimo trimestre' },
  { value: 'all', label: 'Tutto' },
];
const DEPTHS = [
  { value: 'quick', label: 'Rapida' },
  { value: 'standard', label: 'Standard' },
  { value: 'deep', label: 'Approfondita' },
];
const SOURCE_SUGGESTIONS = ['LinkedIn', 'Medium', 'HBR', 'McKinsey', 'MIT Tech Review', 'Forbes', 'TechCrunch'];
const RECURRENCE_DAYS = ['Lunedi', 'Martedi', 'Mercoledi', 'Giovedi', 'Venerdi'];

function relevanceColor(level) {
  const l = (level || '').toLowerCase();
  if (l === 'alta' || l === 'high') return { bg: '#D1FAE5', text: '#059669' };
  if (l === 'media' || l === 'medium') return { bg: '#FEF3C7', text: '#D97706' };
  return { bg: '#F3F4F6', text: '#6B7280' };
}

/* Tag Input inline */
function TagInput({ tags, onChange, placeholder, suggestions, pillColor }) {
  const [input, setInput] = useState('');
  const [showSug, setShowSug] = useState(false);
  const filtered = suggestions?.filter(s => s.toLowerCase().includes(input.toLowerCase()) && !tags.includes(s)) || [];
  const add = (t) => { if (t && !tags.includes(t)) onChange([...tags, t]); setInput(''); setShowSug(false); };
  const remove = (t) => onChange(tags.filter(x => x !== t));
  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 p-1.5 rounded-md border border-input bg-background min-h-[36px] items-center">
        {tags.map(t => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
            style={{ backgroundColor: pillColor || '#64748B' }}>
            {t}<button onClick={() => remove(t)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input className="flex-1 min-w-[80px] bg-transparent text-xs outline-none placeholder:text-muted-foreground px-1"
          value={input} onChange={e => { setInput(e.target.value); setShowSug(true); }}
          onKeyDown={e => { if (e.key === 'Enter' && input.trim()) { e.preventDefault(); add(input.trim()); } }}
          placeholder={tags.length === 0 ? placeholder : ''} />
      </div>
      {showSug && filtered.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-32 overflow-y-auto">
          {filtered.slice(0, 5).map(s => (
            <button key={s} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50" onClick={() => add(s)}>{s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DeepResearchPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('argomenti');
  const [reports, setReports] = useState([]);
  const [searches, setSearches] = useState([]);
  const [searchTypes, setSearchTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [configDialogType, setConfigDialogType] = useState(null);
  const pollingRef = useRef(null);

  // Guided search form
  const [form, setForm] = useState({
    searchType: '', keywords: [], topic: '',
    territories: [], languages: ['IT'],
    period: 'month', preferredSources: [], excludedSources: [],
    depth: 'standard', objective: '',
    scheduleMode: 'now', scheduledDate: '', scheduledTime: '',
    recurring: false, recurrenceFreq: 'weekly', recurrenceDay: 'Lunedi',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rptRes, srcRes, typRes] = await Promise.all([
        api.get('/scout/reports'),
        api.get('/scout/searches'),
        api.get('/content-config/scout-search-types').catch(() => ({ data: [] })),
      ]);
      setReports(rptRes.data || []);
      setSearches(srcRes.data || []);
      setSearchTypes(typRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); return () => { if (pollingRef.current) clearInterval(pollingRef.current); }; }, [loadData]);

  const handleSubmitSearch = async () => {
    if (!form.topic.trim() && form.keywords.length === 0) {
      toast.error('Inserisci un tema o delle keyword');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        search_type_id: form.searchType || undefined,
        keywords: form.keywords.length ? form.keywords : [form.topic],
        query: form.topic,
        territories: form.territories.length ? form.territories : undefined,
        languages: form.languages.length ? form.languages : undefined,
        period: form.period,
        preferred_sources: form.preferredSources.length ? form.preferredSources : undefined,
        excluded_sources: form.excludedSources.length ? form.excludedSources : undefined,
        depth: form.depth,
        objective: form.objective || undefined,
        scheduled_at: form.scheduleMode === 'schedule' && form.scheduledDate
          ? `${form.scheduledDate}T${form.scheduledTime || '09:00'}` : null,
        recurring: form.scheduleMode === 'recurring' ? true : false,
        recurrence_freq: form.scheduleMode === 'recurring' ? form.recurrenceFreq : undefined,
        recurrence_day: form.scheduleMode === 'recurring' ? form.recurrenceDay : undefined,
      };
      await api.post('/scout/searches', payload);
      if (form.scheduleMode === 'now') {
        toast.success('Ricerca avviata — risultati in arrivo');
        // BUG 2.1: Start polling for results
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(async () => {
          try {
            const res = await api.get('/scout/searches');
            const running = (res.data || []).filter(s => s.status === 'running' || s.status === 'pending');
            setSearches(res.data || []);
            if (running.length === 0) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              loadData();
              toast.success('Ricerca completata!');
            }
          } catch {}
        }, 5000);
      } else if (form.scheduleMode === 'schedule') {
        toast.success('Ricerca programmata per ' + form.scheduledDate);
      } else {
        toast.success('Ricerca ricorrente programmata');
      }
      setActiveTab('argomenti');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio ricerca');
    }
    setSubmitting(false);
  };

  const copySource = (result) => {
    const text = `${result.title || ''} - ${result.url || result.source || ''}`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Fonte copiata!');
    }).catch(() => {});
  };

  const handleUseAsSource = (result) => {
    const source = `${result.title || ''} — ${result.url || result.source || ''}`;
    navigate(`/content-studio?topic=${encodeURIComponent(result.title || '')}&inspiration=${encodeURIComponent(source)}`);
  };

  const navigateAction = (action, report) => {
    const topic = report.query || report.title || '';
    const lower = (action || '').toLowerCase();
    if (lower.includes('post') || lower.includes('contenuto') || lower.includes('content')) {
      navigate(`/content-studio?topic=${encodeURIComponent(topic)}`);
    } else if (lower.includes('newsletter')) {
      navigate(`/content-studio?type=newsletter&topic=${encodeURIComponent(topic)}`);
    } else if (lower.includes('speaker') || lower.includes('crm') || lower.includes('contatt')) {
      navigate(`/contacts?search=${encodeURIComponent(topic)}`);
    } else {
      navigate(`/content-studio?topic=${encodeURIComponent(topic)}`);
    }
  };

  const filteredReports = reports.filter(r =>
    !searchFilter || (r.title || r.query || '').toLowerCase().includes(searchFilter.toLowerCase())
  );

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="deep-research-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-warm-accent)' }} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-3" data-testid="deep-research-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-display)' }}>
          Deep Research
        </h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--color-muted)' }}>
          Raccogli e organizza ricerche di mercato, competitor e trend
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b pb-0" style={{ borderColor: '#E5E7EB' }}>
        {[
          { key: 'argomenti', label: 'Argomenti' },
          { key: 'guidata', label: 'Ricerca guidata' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className="text-sm font-medium pb-2 border-b-2 transition-colors px-1"
            style={{
              color: activeTab === tab.key ? '#2563EB' : 'var(--color-muted)',
              borderColor: activeTab === tab.key ? '#2563EB' : 'transparent',
            }}
            data-testid={`tab-${tab.key}`}>
            {tab.label}
          </button>
        ))}
        <div className="flex-1" />
        {activeTab === 'argomenti' && (
          <Button size="sm" onClick={() => setActiveTab('guidata')} data-testid="new-research-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nuovo argomento
          </Button>
        )}
      </div>

      {/* ═══ TAB 1: ARGOMENTI ═══ */}
      {activeTab === 'argomenti' && (
        <div className="flex-1 overflow-y-auto space-y-3">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Cerca argomenti..." value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              className="pl-9 text-sm" data-testid="search-reports" />
          </div>

          {filteredReports.length === 0 && searches.length === 0 ? (
            /* Empty state */
            <div className="flex-1 flex items-center justify-center pt-12">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                  <Search className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-primary)' }}>Esplora il tuo mercato</h3>
                <p className="text-sm mt-1 max-w-xs mx-auto" style={{ color: 'var(--color-muted)' }}>
                  Avvia una ricerca guidata per trovare trend, competitor e fonti da usare nei tuoi contenuti.
                </p>
                <Button className="mt-4" onClick={() => setActiveTab('guidata')} data-testid="start-first-research">
                  <Search className="w-4 h-4 mr-1.5" /> Avvia la prima ricerca
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Active searches */}
              {searches.filter(s => s.status === 'running' || s.status === 'pending').map(s => (
                <Card key={s.id} className="p-3" style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)' }}
                  data-testid={`search-${s.id}`}>
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    <span className="text-sm font-medium flex-1">{s.query || s.keywords?.join(', ') || 'Ricerca'}</span>
                    <Badge variant="outline" className="text-[10px]">{s.status === 'running' ? 'In corso' : 'In attesa'}</Badge>
                  </div>
                </Card>
              ))}

              {/* Scheduled/Recurring searches */}
              {searches.filter(s => s.status === 'scheduled' || s.recurring).length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 mt-2" style={{ color: 'var(--color-muted)' }}>
                    Ricerche programmate
                  </p>
                  {searches.filter(s => s.status === 'scheduled' || s.recurring).map(s => (
                    <Card key={s.id} className="p-3 mb-1.5" style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)' }}
                      data-testid={`scheduled-${s.id}`}>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium flex-1">{s.query || s.keywords?.join(', ') || 'Ricerca'}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {s.recurring ? `Ricorrente (${s.recurrence_freq || 'settimanale'})` : `Programmata ${s.scheduled_at ? new Date(s.scheduled_at).toLocaleDateString('it-CH') : ''}`}
                        </Badge>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Reports */}
              {filteredReports.map(report => (
                <Card key={report.id} className="overflow-hidden" style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)' }}
                  data-testid={`report-${report.id}`}>
                  <button onClick={() => setExpandedId(expandedId === report.id ? null : report.id)}
                    className="w-full text-left p-4 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold flex-1" style={{ color: 'var(--color-primary)' }}>
                        {report.title || report.query || 'Ricerca'}
                      </span>
                      <Badge variant={report.status === 'completed' ? 'default' : 'outline'} className="text-[10px]">
                        {report.status === 'completed' ? 'Attivo' : report.status || 'Attivo'}
                      </Badge>
                      {expandedId === report.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px]" style={{ color: 'var(--color-muted)' }}>
                      <span>{report.category || report.search_type || 'Mercato'}</span>
                      <span>{report.results?.length || 0} fonti</span>
                      <span>{report.created_at ? new Date(report.created_at).toLocaleDateString('it-CH') : ''}</span>
                    </div>
                  </button>

                  {expandedId === report.id && (
                    <div className="border-t px-4 pb-4 space-y-4">
                      {/* Analisi Scout */}
                      {report.analysis && (
                        <div className="mt-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--color-muted)' }}>Analisi Scout</p>
                          <div className="rounded-lg p-3 text-sm leading-relaxed" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
                            data-testid="report-analysis">
                            {report.analysis}
                          </div>
                        </div>
                      )}

                      {/* Azioni suggerite */}
                      {report.suggested_actions?.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--color-muted)' }}>Azioni suggerite</p>
                          <div className="space-y-1.5">
                            {report.suggested_actions.map((action, i) => (
                              <button key={i} onClick={() => navigateAction(action, report)}
                                className="w-full text-left p-2.5 rounded-lg border hover:border-blue-400 hover:bg-blue-50/50 transition-all flex items-center justify-between group"
                                data-testid={`action-${i}`}>
                                <span className="text-xs">{action}</span>
                                <span className="text-[11px] text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {action.toLowerCase().includes('newsletter') ? 'Inserisci in newsletter' :
                                    action.toLowerCase().includes('crm') || action.toLowerCase().includes('speaker') ? 'Vedi nel CRM' :
                                    'Crea nel Content Studio'} &rarr;
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risultati */}
                      {report.results?.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--color-muted)' }}>
                            Risultati ({report.results.length})
                          </p>
                          <div className="space-y-2">
                            {report.results.map((r, i) => {
                              const rel = relevanceColor(r.relevance);
                              return (
                                <div key={i} className="rounded-lg border p-3" style={{ backgroundColor: '#FFFFFF' }}
                                  data-testid={`result-${i}`}>
                                  <div className="flex items-start gap-2">
                                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>
                                        {r.title}
                                      </p>
                                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-muted)' }}>
                                        {r.source} {r.date ? `\u00B7 ${r.date}` : ''}
                                      </p>
                                      {(r.description || r.snippet) && (
                                        <p className="text-[11px] mt-1.5 line-clamp-2" style={{ color: '#4B5563' }}>
                                          {r.description || r.snippet}
                                        </p>
                                      )}
                                      <div className="flex items-center gap-2 mt-2">
                                        {r.relevance && (
                                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                                            style={{ backgroundColor: rel.bg, color: rel.text }}>
                                            Rilevanza: {r.relevance}
                                          </span>
                                        )}
                                        <button onClick={() => handleUseAsSource(r)}
                                          className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                                          data-testid={`use-source-${i}`}>
                                          <Copy className="w-3 h-3" /> Usa nel Content Studio
                                        </button>
                                        {(r.url || r.link) && (
                                          <a href={r.url || r.link} target="_blank" rel="noopener noreferrer"
                                            className="text-[11px] text-blue-600 hover:underline flex items-center gap-1">
                                            <ExternalLink className="w-3 h-3" /> Apri
                                          </a>
                                        )}
                                        <button className="text-[11px] text-muted-foreground hover:text-blue-600 flex items-center gap-1">
                                          <Bookmark className="w-3 h-3" /> Salva
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'guidata' && (
        <div className="flex-1 overflow-y-auto">
          <Card className="p-5 space-y-4" style={{ boxShadow: 'var(--shadow-card)', borderRadius: 'var(--radius-soft)', maxWidth: 560, border: 'none' }}
            data-testid="guided-search-form">
            <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted)' }}>
              Parametri di ricerca
            </p>

            {/* Tipo ricerca */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-medium" style={{ color: 'var(--color-muted)' }}>Tipo ricerca</label>
                <button onClick={() => setConfigDialogType('scout-search-types')}
                  className="text-[10px] flex items-center gap-1 transition-colors"
                  style={{ color: 'var(--color-muted)' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#2563EB'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
                  data-testid="gear-scout-search-types">
                  &#9881; modifica
                </button>
              </div>
              {searchTypes.length > 0 ? (
                <Select value={form.searchType} onValueChange={v => setForm(p => ({ ...p, searchType: v }))}>
                  <SelectTrigger className="text-xs h-9" data-testid="search-type-select"><SelectValue placeholder="Seleziona tipo..." /></SelectTrigger>
                  <SelectContent>{searchTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label || t.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Select value={form.searchType} onValueChange={v => setForm(p => ({ ...p, searchType: v }))}>
                  <SelectTrigger className="text-xs h-9" data-testid="search-type-select"><SelectValue placeholder="Tipo ricerca..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trend">Trend di settore</SelectItem>
                    <SelectItem value="competitor">Analisi competitor</SelectItem>
                    <SelectItem value="audience">Ricerca audience</SelectItem>
                    <SelectItem value="content">Ispirazione contenuti</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Tema / Keywords */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Tema / Keywords</label>
              <Textarea placeholder="Descrivi cosa vuoi cercare..." value={form.topic}
                onChange={e => setForm(p => ({ ...p, topic: e.target.value }))}
                rows={2} className="text-xs mb-1.5" data-testid="search-topic" />
              <TagInput tags={form.keywords} onChange={v => setForm(p => ({ ...p, keywords: v }))}
                placeholder="Aggiungi keyword..." pillColor="#3B82F6" />
            </div>

            {/* Territorio */}
            <div>
              <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-muted)' }}>Territorio</label>
              <div className="flex flex-wrap gap-1.5">
                {TERRITORIES.map(t => (
                  <Badge key={t} variant={form.territories.includes(t) ? 'default' : 'outline'}
                    className="cursor-pointer text-[11px] px-2 py-0.5"
                    onClick={() => setForm(p => ({
                      ...p, territories: p.territories.includes(t) ? p.territories.filter(x => x !== t) : [...p.territories, t]
                    }))}
                    data-testid={`territory-${t}`}>{t}</Badge>
                ))}
              </div>
            </div>

            {/* Lingua fonti */}
            <div>
              <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-muted)' }}>Lingua fonti</label>
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGES.map(l => (
                  <Badge key={l} variant={form.languages.includes(l) ? 'default' : 'outline'}
                    className="cursor-pointer text-[11px] px-2 py-0.5"
                    onClick={() => setForm(p => ({
                      ...p, languages: p.languages.includes(l) ? p.languages.filter(x => x !== l) : [...p.languages, l]
                    }))}
                    data-testid={`lang-${l}`}>{l}</Badge>
                ))}
              </div>
            </div>

            {/* Periodo */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Periodo</label>
              <Select value={form.period} onValueChange={v => setForm(p => ({ ...p, period: v }))}>
                <SelectTrigger className="text-xs h-9" data-testid="period-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Fonti preferite */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Fonti preferite</label>
              <TagInput tags={form.preferredSources} onChange={v => setForm(p => ({ ...p, preferredSources: v }))}
                placeholder="Aggiungi fonte..." pillColor="#22C55E" suggestions={SOURCE_SUGGESTIONS} />
            </div>

            {/* Fonti da escludere */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Fonti da escludere</label>
              <TagInput tags={form.excludedSources} onChange={v => setForm(p => ({ ...p, excludedSources: v }))}
                placeholder="Aggiungi fonte da escludere..." pillColor="#EF4444" />
            </div>

            {/* Profondità */}
            <div>
              <label className="text-[11px] font-medium block mb-1.5" style={{ color: 'var(--color-muted)' }}>Profondita</label>
              <div className="grid grid-cols-3 gap-1.5">
                {DEPTHS.map(d => (
                  <button key={d.value} onClick={() => setForm(p => ({ ...p, depth: d.value }))}
                    className={`py-2 rounded-md text-xs font-medium border transition-colors ${form.depth === d.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`depth-${d.value}`}>{d.label}</button>
                ))}
              </div>
            </div>

            {/* Obiettivo */}
            <div>
              <label className="text-[11px] font-medium block mb-1" style={{ color: 'var(--color-muted)' }}>Obiettivo della ricerca</label>
              <Textarea placeholder="Perche cerchi? Per un post, un articolo, un evento?" value={form.objective}
                onChange={e => setForm(p => ({ ...p, objective: e.target.value }))}
                rows={2} className="text-xs" data-testid="search-objective" />
            </div>

            {/* Programmazione */}
            <div className="border-t pt-3">
              <p className="text-[12px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-muted)' }}>
                Programmazione
              </p>
              <div className="space-y-2">
                {[
                  { value: 'now', label: 'Esegui ora' },
                  { value: 'schedule', label: 'Programma' },
                  { value: 'recurring', label: 'Ricorrente' },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer" data-testid={`schedule-${opt.value}`}>
                    <input type="radio" name="scheduleMode" checked={form.scheduleMode === opt.value}
                      onChange={() => setForm(p => ({ ...p, scheduleMode: opt.value }))}
                      className="rounded-full" />
                    <span className="text-xs font-medium">{opt.label}</span>
                  </label>
                ))}
                {form.scheduleMode === 'schedule' && (
                  <div className="grid grid-cols-2 gap-2 ml-6">
                    <Input type="date" value={form.scheduledDate}
                      onChange={e => setForm(p => ({ ...p, scheduledDate: e.target.value }))}
                      className="text-xs h-8" data-testid="schedule-date" />
                    <Input type="time" value={form.scheduledTime}
                      onChange={e => setForm(p => ({ ...p, scheduledTime: e.target.value }))}
                      className="text-xs h-8" data-testid="schedule-time" />
                  </div>
                )}
                {form.scheduleMode === 'recurring' && (
                  <div className="grid grid-cols-2 gap-2 ml-6">
                    <Select value={form.recurrenceFreq} onValueChange={v => setForm(p => ({ ...p, recurrenceFreq: v }))}>
                      <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Settimanale</SelectItem>
                        <SelectItem value="biweekly">Bisettimanale</SelectItem>
                        <SelectItem value="monthly">Mensile</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={form.recurrenceDay} onValueChange={v => setForm(p => ({ ...p, recurrenceDay: v }))}>
                      <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{RECURRENCE_DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-2 pt-2">
              {form.scheduleMode === 'now' ? (
                <Button onClick={handleSubmitSearch} disabled={submitting} className="flex-1" data-testid="submit-search">
                  {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
                  Avvia ricerca
                </Button>
              ) : (
                <Button onClick={handleSubmitSearch} disabled={submitting} className="flex-1" data-testid="schedule-search">
                  {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Clock className="w-4 h-4 mr-1.5" />}
                  {form.scheduleMode === 'schedule' ? 'Programma ricerca' : 'Programma ricerca ricorrente'}
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Config Dialog */}
      <ConfigDialog open={!!configDialogType} onClose={() => setConfigDialogType(null)}
        configType={configDialogType} onUpdate={loadData} />
    </div>
  );
}
